// The technician VPN's API (docs/VpnAccess.md): sessions, gateways, DNS domains
// and the caller's client devices. Flow records and the VPN settings they come
// with are SaaS-only and live in `@/features/vpnFlows`.
import axios from 'axios';
import { config } from '@/config';

const api = (path: string) => `${config.backendApi}/api${path}`;
const opts = { withCredentials: true } as const;

export interface VpnSession {
  id: string;
  gatewayId: string;
  gatewayName: string | null;
  userId: string;
  /** "First Last"; null when the user has no name. */
  userName: string | null;
  userEmail: string | null;
  clientDeviceId: string;
  clientDeviceName: string | null;
  allowedPrefixes: string[];
  requestedAt: string;
  /** The client's first handshake with the gateway. */
  startedAt: string | null;
  expiresAt: string;
  endedAt: string | null;
  endReason: string | null;
  endpoint: string | null;
  lastHandshakeAt: string | null;
  bytesFromClient: number;
  bytesToClient: number;
  /** Why the gateway doesn't hold the session, if it refused it. */
  gatewayRefused: string | null;
  /** The version of the Witina app that turned it on; null for a session made through the API. */
  appVersion: string | null;
  /** Its IPv4 aliases; `alias` is null for a range no block was free for. Absent from a backend before step 5. */
  ipv4Aliases?: Ipv4Alias[];
}

/** One of a session's IPv4 aliases: a host in `alias` is the same host in `real`. */
export interface Ipv4Alias {
  real: string;
  alias: string | null;
}

export interface VpnSessionPage {
  sessions: VpnSession[];
  total: number;
  page: number;
  pageSize: number;
}

export interface VpnSessionQuery {
  /** Sessions made to this region. */
  regionId?: string;
  /** Sessions this gateway carries. */
  gatewayId?: string;
  /** true: live only; false: ended only; undefined: both. */
  live?: boolean;
  page?: number;
  pageSize?: number;
}

export async function fetchSessions(query: VpnSessionQuery): Promise<VpnSessionPage> {
  const params: Record<string, string> = {};
  if (query.regionId) params.regionId = query.regionId;
  if (query.gatewayId) params.gatewayId = query.gatewayId;
  if (query.live !== undefined) params.live = String(query.live);
  if (query.page) params.page = String(query.page);
  if (query.pageSize) params.pageSize = String(query.pageSize);
  const response = await axios.get<VpnSessionPage>(api('/vpn/sessions'), { ...opts, params });
  return response.data;
}

export async function endSession(id: string): Promise<void> {
  await axios.delete(api(`/vpn/sessions/${id}`), opts);
}

/** What the gateway last said about its VPN. */
export interface VpnReport {
  /** Whether the VPN is on at the gateway, as it says. */
  on: boolean;
  error: string | null;
  publicKey: string | null;
  listenPort: number | null;
  candidates: { address: string; kind: string }[];
  natBehaviour: string;
  stunAnswers: { server: string; mapped?: string; error?: string }[];
  /** How many sessions the gateway holds. */
  sessions: number;
  gatewayTime: string | null;
  reportedAt: string;
  /** What its split-DNS resolver forwards to now, and why it can't answer. */
  dnsServers: string[];
  dnsError: string | null;
  flowsLost: number;
  flowsQueued: number;
  /** While it's on: what its host may still do to the VPN's traffic that the gateway can't fix.
   *  Absent from a backend before slice 14. */
  warnings?: string[];
}

/** A gateway's VPN: its region's setting, and what the gateway can do and says. */
export interface GatewayVpn {
  /** The region whose setting it follows. */
  regionId: string;
  regionName: string;
  /** Whether its region has the VPN on. */
  enabled: boolean;
  /** Its region's DNS servers; none means the gateway's own. */
  dnsServers: string[];
  available: boolean;
  unavailableReason: string | null;
  report: VpnReport | null;
}

export interface GatewayVpnListItem {
  gatewayId: string;
  gatewayName: string | null;
  vpn: GatewayVpn;
}

export async function fetchGatewayVpn(gatewayId: string): Promise<GatewayVpn> {
  const response = await axios.get<GatewayVpn>(api(`/gateways/${gatewayId}/vpn`), opts);
  return response.data;
}

/** The VPN in a region (docs/VpnAccess.md, "Slice 7 design"): its setting, which
 *  every gateway in it follows, and each gateway. */
export interface RegionVpn {
  regionId: string;
  regionName: string;
  divisionId: string | null;
  enabled: boolean;
  /** The site's DNS servers; none means each gateway's own. */
  dnsServers: string[];
  /** What a session made now reaches: networks seen on its devices and those added, merged. */
  routes: RegionRoute[];
  /** The networks an administrator added. */
  extraPrefixes: string[];
  gateways: GatewayVpnListItem[];
}

/** One of a region's VPN routes, and what it was made from. */
export interface RegionRoute {
  prefix: string;
  sources: ('seen' | 'added')[];
}

export async function fetchRegionVpns(): Promise<RegionVpn[]> {
  const response = await axios.get<{ regions: RegionVpn[] }>(api('/vpn/regions'), opts);
  return response.data.regions;
}

export async function fetchRegionVpn(regionId: string): Promise<RegionVpn> {
  const response = await axios.get<RegionVpn>(api(`/regions/${regionId}/vpn`), opts);
  return response.data;
}

export interface SetRegionVpn {
  enabled: boolean;
  /** Left out, they stay as they are; empty for each gateway's own. */
  dnsServers?: string[];
  /** Networks to add to its routes. Left out, they stay as they are. */
  extraPrefixes?: string[];
}

export async function setRegionVpn(regionId: string, body: SetRegionVpn): Promise<RegionVpn> {
  const response = await axios.put<RegionVpn>(api(`/regions/${regionId}/vpn`), body, opts);
  return response.data;
}

/** One scope's DNS domains: the organization's (`divisionId` null) or a division's. */
export interface DnsDomains {
  divisionId: string | null;
  domains: string[];
}

export async function fetchDnsDomains(): Promise<DnsDomains[]> {
  const response = await axios.get<{ lists: DnsDomains[] }>(api('/vpn/dns-domains'), opts);
  return response.data.lists;
}

export async function setDnsDomains(body: DnsDomains): Promise<DnsDomains> {
  const response = await axios.put<DnsDomains>(api('/vpn/dns-domains'), body, opts);
  return response.data;
}

export interface VpnClientDevice {
  id: string;
  name: string;
  publicKey: string;
  platform: string;
  appVersion: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
}

export async function fetchMyDevices(): Promise<VpnClientDevice[]> {
  const response = await axios.get<{ devices: VpnClientDevice[] }>(api('/vpn/client-devices'), opts);
  return response.data.devices;
}

export async function revokeDevice(id: string): Promise<void> {
  await axios.delete(api(`/vpn/client-devices/${id}`), opts);
}

/** A VPN policy (docs/VpnAccess.md, "Policies"): these teams may connect to this
 *  scope for at most this long. */
export interface VpnPolicy {
  id: string;
  name: string;
  enabled: boolean;
  /** `full_network` until finer-grained access ships. */
  access: string;
  maxSessionMinutes: number;
  /** Every site in the organization; otherwise `places`. */
  wholeOrganization: boolean;
  places: VpnPolicyPlace[];
  teams: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
}

/** A place a policy covers: every site in a division, or one site. */
export interface VpnPolicyPlace {
  kind: 'division' | 'region';
  id: string;
  name: string;
}

export interface VpnPolicyInput {
  name: string;
  enabled: boolean;
  teamIds: string[];
  /** Every site; or at least one division or region. */
  wholeOrganization: boolean;
  divisionIds: string[];
  regionIds: string[];
  maxSessionMinutes: number;
}

export async function fetchPolicies(): Promise<VpnPolicy[]> {
  const response = await axios.get<{ policies: VpnPolicy[] }>(api('/vpn/policies'), opts);
  return response.data.policies;
}

export async function createPolicy(body: VpnPolicyInput): Promise<VpnPolicy> {
  const response = await axios.post<VpnPolicy>(api('/vpn/policies'), body, opts);
  return response.data;
}

export async function updatePolicy(id: string, body: VpnPolicyInput): Promise<VpnPolicy> {
  const response = await axios.put<VpnPolicy>(api(`/vpn/policies/${id}`), body, opts);
  return response.data;
}

export async function deletePolicy(id: string): Promise<void> {
  await axios.delete(api(`/vpn/policies/${id}`), opts);
}

export type PathSetting = 'no_preference' | 'direct_preferred' | 'relay_preferred' | 'relay_only' | 'direct_only';

export interface PathSettings {
  organization: PathSetting;
  /** `null`: the organization's applies. */
  divisions: { divisionId: string; pathSetting: PathSetting | null }[];
}

export async function fetchPathSettings(): Promise<PathSettings> {
  const response = await axios.get<PathSettings>(api('/vpn/path-settings'), opts);
  return response.data;
}

/** The organization's (`divisionId` null) or a division's (`pathSetting` null: the organization's). */
export async function setPathSetting(body: { divisionId: string | null; pathSetting: PathSetting | null }): Promise<PathSettings> {
  const response = await axios.put<PathSettings>(api('/vpn/path-settings'), body, opts);
  return response.data;
}

/** A site the caller may connect to, by the policies. */
export interface ReachableSite {
  regionId: string;
  regionName: string;
  divisionId: string | null;
  enabled: boolean;
  /** On, and a gateway there can take a session now. */
  available: boolean;
  maxSessionMinutes: number;
}

export async function fetchReachable(): Promise<ReachableSite[]> {
  const response = await axios.get<{ sites: ReachableSite[] }>(api('/vpn/reachable'), opts);
  return response.data.sites;
}

// ---- The control page (docs/VpnAccess.md, "Slice 9 design"): a run of the
// Witina app, bound to the caller from its link, and the sites it turns on.

/** The same-machine check made when the app was bound. */
export interface AppBindingCheck {
  appAddresses: string[];
  browserAddress: string | null;
  browserOs: string | null;
  addressMatched: boolean;
  osMatched: boolean;
  /** Turning a site on waits for the caller to tick through a mismatch. */
  needsAcknowledgement: boolean;
}

export interface AppBindingSession {
  id: string;
  regionId: string;
  regionName: string;
  organizationId: string;
  expiresAt: string;
  /** The app's first handshake: until then, connecting. */
  startedAt: string | null;
  /** Routes the app skipped, from its latest report. */
  skippedRoutes: string[];
  /** Its IPv4 aliases. Absent from a backend before step 5. */
  ipv4Aliases?: Ipv4Alias[];
}

export interface AppBinding {
  id: string;
  name: string;
  platform: string;
  appVersion: string | null;
  /** Its socket is open now. */
  connected: boolean;
  boundAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  check: AppBindingCheck;
  sessions: AppBindingSession[];
}

export interface SiteDivision {
  /** Empty for the sites with no division. */
  id: string | null;
  name: string | null;
  sites: ReachableSite[];
}

export interface SiteOrganization {
  id: string;
  name: string;
  divisions: SiteDivision[];
}

export async function fetchSites(): Promise<SiteOrganization[]> {
  const response = await axios.get<{ organizations: SiteOrganization[] }>(api('/vpn/sites'), opts);
  return response.data.organizations;
}

export async function bindApp(code: string): Promise<AppBinding> {
  const response = await axios.post<AppBinding>(api('/vpn/app-bindings/bind'), { code }, opts);
  return response.data;
}

export async function fetchAppBinding(id: string): Promise<AppBinding> {
  const response = await axios.get<AppBinding>(api(`/vpn/app-bindings/${id}`), opts);
  return response.data;
}

export async function fetchAppBindings(): Promise<AppBinding[]> {
  const response = await axios.get<{ bindings: AppBinding[] }>(api('/vpn/app-bindings'), opts);
  return response.data.bindings;
}

export async function acknowledgeApp(id: string): Promise<AppBinding> {
  const response = await axios.post<AppBinding>(api(`/vpn/app-bindings/${id}/acknowledge`), {}, opts);
  return response.data;
}

export async function disconnectApp(id: string): Promise<void> {
  await axios.delete(api(`/vpn/app-bindings/${id}`), opts);
}

export async function turnSiteOn(bindingId: string, regionId: string): Promise<AppBinding> {
  const response = await axios.post<AppBinding>(api(`/vpn/app-bindings/${bindingId}/sessions`), { regionId }, opts);
  return response.data;
}

export async function turnSiteOff(bindingId: string, sessionId: string): Promise<void> {
  await axios.delete(api(`/vpn/app-bindings/${bindingId}/sessions/${sessionId}`), opts);
}

export async function extendSite(bindingId: string, sessionId: string, minutes: number): Promise<AppBinding> {
  const response = await axios.patch<AppBinding>(api(`/vpn/app-bindings/${bindingId}/sessions/${sessionId}`), { minutes }, opts);
  return response.data;
}

/** An installer the caller's channel offers (docs/VpnAppReleases.md,
 *  "Downloads for people"). */
export interface AppDownload {
  /** `linux-amd64`, `windows-x86_64` or `macos-universal`. */
  platform: string;
  version: string;
  file: string;
  size: number;
  minimumOs: string | null;
  url: string;
}

export interface AppDownloads {
  /** `beta` if any of the caller's organizations is on beta. */
  channel: string;
  downloads: AppDownload[];
}

export async function fetchAppDownloads(): Promise<AppDownloads> {
  const response = await axios.get<AppDownloads>(api('/vpn/app/downloads'), opts);
  return response.data;
}
